import {ReactElement, useContext, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {BudgetingType} from '../../../../../BudgetingType';
import {getBudgetYearForProperty, useProperties} from '../../../../../contexts/properties/PropertiesContext';
import {
    AffordableInPlaceRentChangeInput,
    AffordableRevenueModelInPlaceRentChangeType,
    useGetAffordableInPlaceRentChangeIncreasesLazyQuery,
    useGetForecastLocksLazyQuery,
    useUpsertAffordableInPlaceRentChangeIncreasesMutation,
    VersionType,
} from '../../../../../__generated__/generated_types';
import "native-injects";

import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../../../../styles/zendesk-garden/ViziblyZDGTheme";

import {Tabs} from "@zendeskgarden/react-tabs";

import * as workflowCSS from "../../../../../styles/workflows/workflowCSS.module.scss";
import * as workflowHeaderCSS from "../../../../../components/workflows/workflow-header/styles/css.module.scss";

import WorkflowHeader from "../../../../../components/workflows/workflow-header/WorkflowHeader";
import WorkflowNavToggle from "../../../../../components/workflows/workflow-nav-toggle/WorkflowNavToggle";
import WorkflowPageLabel from "../../../../../components/workflows/workflow-page-label/WorkflowPageLabel";
import WorkflowStepNav from "../../../../../components/workflows/workflow-step-nav/WorkflowStepNav";

import WorkflowYearTabs, {
    WORKFLOW_YEAR_TAB_BUDGET,
} from "../../../../../components/workflows/workflow-year-tabs/WorkflowYearTabs";
import {IWorkflowPageProps, shouldUpdateWorkflowTab} from "../../../logic/workflows";
import {IVZHotTableRow} from '../../../../../components/vz-ui/vz-hot-table/VZHotTable';
import cn from "classnames";
import VZSaveStatus from '../../../../../components/vz-ui/vz-save-status/VZSaveStatus';
import {VZHotTableConfig} from '../../../../../components/vz-ui/vz-hot-table/vz-hot-table-config';
import OperationalVZHotTable from '../../OperationalVZHotTable';
import {getMonths} from '../../../../../utils/months-helper';
import {AuthContext} from "../../../../../contexts/AuthContext";

const ROW_LABELS = [
    "Subsidized Tenant Portion Change",
    "Subsidy Change",
] as const;

type RentChangeField = "subsidizedResidentialRentChange" | "subsidyChange";
type RentChangeTypeField = "subsidizedResidentialRentChangeType" | "subsidyChangeType";

const ROW_FIELD_MAP: {label: string, valueField: RentChangeField, typeField: RentChangeTypeField}[] = [
    {label: ROW_LABELS[0], valueField: "subsidizedResidentialRentChange", typeField: "subsidizedResidentialRentChangeType"},
    {label: ROW_LABELS[1], valueField: "subsidyChange", typeField: "subsidyChangeType"},
];

export function AffordableInPlaceRentChange(props: IWorkflowPageProps): ReactElement {
    const {type} = useParams() as { type: BudgetingType };
    const auth = useContext(AuthContext);
    const {currentProperty} = useProperties();
    const budgetYear = getBudgetYearForProperty(currentProperty, auth.budgetYear);

    const versionType = type == BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast;

    const [selectedTab, setSelectedTab] = useState(WORKFLOW_YEAR_TAB_BUDGET);
    const [lastSaved, setLastSaved] = useState<Date>(new Date());

    const [getForecastLocks, {data: forecastLocks}] = useGetForecastLocksLazyQuery({fetchPolicy: "no-cache"});
    const [getInPlaceRentChange, {data: rentChangeData, loading: dataLoading}] = useGetAffordableInPlaceRentChangeIncreasesLazyQuery({fetchPolicy: "no-cache"});
    const [upsertInPlaceRentChange, {loading: savingValues}] = useUpsertAffordableInPlaceRentChangeIncreasesMutation({ignoreResults: true});

    useEffect(() => {
        if (currentProperty) {
            getForecastLocks({
                variables: {
                    propertyId: currentProperty.id,
                    budgetYear: currentProperty.budgetYear
                }
            });
            fetchData();
        }
    }, [currentProperty]);

    useEffect(() => {
        const updateWorkflowTab = shouldUpdateWorkflowTab(type, selectedTab);
        if (updateWorkflowTab != false) {
            setSelectedTab(updateWorkflowTab as string);
        }
        fetchData();
    }, [type]);

    function fetchData() {
        if (!currentProperty) {
            return;
        }

        getInPlaceRentChange({
            variables: {
                propertyId: currentProperty.id,
                budgetYear: budgetYear,
                versionType: versionType,
            }
        });
    }

    const {tableData, tableConfig} = useMemo<{tableData: IVZHotTableRow[], tableConfig: VZHotTableConfig}>(() => {
        if (!forecastLocks?.forecastLocks || !rentChangeData || !currentProperty) {
            return {tableData: [], tableConfig: new VZHotTableConfig()};
        }

        const data = rentChangeData.affordableInPlaceRentChangeIncreases;
        const tableLocked = versionType == VersionType.Budget
            ? forecastLocks.forecastLocks.property.budgetLocked
            : forecastLocks.forecastLocks.property.reforecastLocked;

        const tableData: IVZHotTableRow[] = ROW_FIELD_MAP.map(({label, valueField, typeField}) => {
            const values = data[valueField].map((v, i) => {
                const changeType = data[typeField][i];
                const numVal = v != null ? parseFloat(v.toString()) : 0;
                return changeType === AffordableRevenueModelInPlaceRentChangeType.Percent
                    ? numVal * 100
                    : numVal;
            });

            return {
                label,
                rowType: "generic",
                values,
                rowSummaryFunction: (_row, _col, values) => {
                    const toAvg = values.filter(v => v > 0.0001);
                    if (toAvg.length == 0) {
                        return 0;
                    }
                    return toAvg.sum() / toAvg.length;
                }
            };
        });

        const tableConfig = new VZHotTableConfig();
        const startMonthIndex = versionType == VersionType.Budget ? 0 : currentProperty.reforecastStartMonthIndex;

        for (let rowIdx = 0; rowIdx < tableData.length; rowIdx++) {
            for (let colIdx = 0; colIdx < 12; colIdx++) {
                const isLocked = tableLocked || colIdx < startMonthIndex;
                tableConfig.applyToColumn(colIdx + 1, {
                    isReadOnly: isLocked
                });
            }
        }

        tableConfig.applyToColumnAndFollowing(1, {format: "percent"});

        return {tableData, tableConfig};
    }, [rentChangeData, forecastLocks?.forecastLocks]);

    function handleChange(changes: [number, number, number][]) {
        if (!currentProperty || !rentChangeData) {
            return;
        }

        const data = rentChangeData.affordableInPlaceRentChangeIncreases;
        const inputByMonth = new Map<number, AffordableInPlaceRentChangeInput>();

        for (const [rowIndex, monthIndex, value] of changes) {
            const rowMapping = ROW_FIELD_MAP[rowIndex];
            if (!rowMapping) continue;

            const changeType = data[rowMapping.typeField][monthIndex] ?? AffordableRevenueModelInPlaceRentChangeType.Percent;
            const adjustedValue = changeType === AffordableRevenueModelInPlaceRentChangeType.Percent
                ? (value / 100.0).toString()
                : value.toString();

            const existing = inputByMonth.get(monthIndex) ?? {monthIndex};
            inputByMonth.set(monthIndex, {
                ...existing,
                [rowMapping.valueField]: adjustedValue,
                [rowMapping.typeField]: changeType,
            });
        }

        const inputData = Array.from(inputByMonth.values());

        upsertInPlaceRentChange({
            variables: {
                propertyId: currentProperty.id,
                budgetYear: budgetYear,
                versionType: versionType,
                data: inputData,
            }
        }).then(res => {
            if(res.data?.upsertAffordableInPlaceRentChangeIncreases) {
                setLastSaved(new Date());
            }
        });
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <div className={cn(workflowCSS.workflowContainer, workflowCSS.revenueScreen)}>
                <WorkflowHeader>
                    <WorkflowHeader.LeftCol>
                        <div className={workflowHeaderCSS.rowItemsContainer}>
                            <WorkflowNavToggle inUserWorkflow={props.inUserWorkflow}/>
                            <WorkflowPageLabel label="In-Place Rent Change"/>
                        </div>
                    </WorkflowHeader.LeftCol>
                    <WorkflowHeader.RightCol>
                        <VZSaveStatus isSaving={savingValues} lastSaved={lastSaved}/>
                        <WorkflowStepNav onPreviousClick={props.onPreviousClick} onNextClick={props.onNextClick}
                                         inUserWorkflow={props.inUserWorkflow}/>
                    </WorkflowHeader.RightCol>
                </WorkflowHeader>
                <div className={workflowCSS.bodyContainer}>
                    {currentProperty &&
                        <>
                            <Tabs selectedItem={selectedTab} onChange={setSelectedTab}
                                  className={workflowCSS.tabsContainer}>
                                <WorkflowYearTabs
                                    property={currentProperty}
                                    selectedTab={selectedTab}
                                    rawForecastLocks={forecastLocks}
                                />
                            </Tabs>

                            <div className={workflowCSS.tableWrapper}>
                                <OperationalVZHotTable
                                    data={tableData}
                                    colWidths={[200]}
                                    colHeaders={["Category", ...getMonths(currentProperty), "Average"]}
                                    fixedRowsTop={0}
                                    fixedRowsBottom={0}
                                    handsontableProps={{}}
                                    tableConfig={tableConfig}
                                    isDataLoading={dataLoading}
                                    isUndoRedoable
                                    onChange={handleChange}
                                />
                            </div>
                        </>
                    }
                </div>
            </div>
        </ThemeProvider>
    );
}
